import { api } from '../../lib/api.js';
import { getAppConfig } from '../../lib/app-config.js';
import { BaseComponent } from '../../lib/base-component.js';
import { css, html, nothing } from '../../lib/lit.js';
import { portalPollUrl, RESULTS_VISIBILITIES, STATE_BADGE } from '../../lib/polls.js';
import '../app/share-link.js';

function formatDate(iso) {
    if (!iso) return '';
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

function visibilityLabel(value) {
    return RESULTS_VISIBILITIES.find((v) => v.value === value)?.label ?? value;
}

// Every poll, drafts included. Polls live in d3-community and are answered on
// the community portal; this lists them through the Staff API (which forwards
// the staff member's own token) and links each to the editor and its results.
class PollsView extends BaseComponent {
    static properties = {
        /** @type {Array<object>} */
        _polls: { state: true },
        _loading: { state: true },
        _error: { state: true },
    };

    static styles = css`
        :host {
            display: block;
        }
        .wrap {
            max-width: 64rem;
            margin: 0 auto;
            padding: 2rem 1rem 4rem;
        }
        table {
            font-size: 0.9rem;
        }
    `;

    constructor() {
        super();
        this._polls = [];
        this._loading = true;
        this._error = '';
    }

    connectedCallback() {
        super.connectedCallback();
        this._load();
    }

    async _load() {
        this._loading = true;
        this._error = '';
        try {
            const body = await api.get('/polls');
            this._polls = body.polls;
        } catch (error) {
            this._error = error.message;
        } finally {
            this._loading = false;
        }
    }

    render() {
        return html`
            <div class="wrap">
                <div class="d-flex align-items-center gap-2 mb-4">
                    <h1 class="h4 mb-0">Polls</h1>
                    <button
                        class="btn btn-sm btn-outline-secondary"
                        type="button"
                        ?disabled=${this._loading}
                        @click=${this._load}
                    >
                        Refresh
                    </button>
                    <a class="btn btn-sm btn-primary ms-auto" href="/polls/new">
                        <i class="bi bi-plus-lg"></i> New poll
                    </a>
                </div>
                <p class="text-muted">
                    Polls members answer on the community portal. Drafts are visible only here until
                    opened.
                </p>
                ${this._renderBody()}
            </div>
        `;
    }

    _renderBody() {
        if (this._error) {
            return html`<div class="alert alert-danger" role="alert">${this._error}</div>`;
        }
        if (this._loading && this._polls.length === 0) {
            return html`<p class="text-muted">Loading…</p>`;
        }
        if (this._polls.length === 0) {
            return html`<p class="text-muted">No polls yet.</p>`;
        }
        const portalUrl = getAppConfig().communityPortalUrl;
        return html`
            <div class="table-responsive">
                <table class="table table-sm table-hover align-middle">
                    <thead>
                        <tr>
                            <th>Title</th>
                            <th>Status</th>
                            <th>Results</th>
                            <th>Changes</th>
                            <th>Responses</th>
                            <th>Opened</th>
                            <th>Closes</th>
                            <th>Portal</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${this._polls.map(
                            (poll) => html`
                                <tr>
                                    <td>
                                        <a href="/polls/${poll.id}">${poll.title || html`<em>Untitled</em>`}</a>
                                    </td>
                                    <td><span class="badge ${STATE_BADGE[poll.state]}">${poll.state}</span></td>
                                    <td class="text-muted">${visibilityLabel(poll.resultsVisibility)}</td>
                                    <td class="text-muted">${poll.allowChanges ? 'Allowed' : 'Final'}</td>
                                    <td>
                                        ${
                                            poll.state === 'draft'
                                                ? html`<span class="text-muted">—</span>`
                                                : html`<a href="/polls/${poll.id}/results"
                                                      >${poll.responseCount}
                                                      <i class="bi bi-bar-chart small"></i
                                                  ></a>`
                                        }
                                    </td>
                                    <td class="text-muted">${formatDate(poll.openedAt)}</td>
                                    <td class="text-muted">
                                        ${
                                            poll.closedAt
                                                ? html`Closed ${formatDate(poll.closedAt)}`
                                                : poll.closesAt
                                                  ? formatDate(poll.closesAt)
                                                  : nothing
                                        }
                                    </td>
                                    <td>
                                        ${
                                            poll.state === 'draft'
                                                ? nothing
                                                : html`<d3-share-link
                                                      label="Link"
                                                      href=${portalPollUrl(portalUrl, poll.id)}
                                                  ></d3-share-link>`
                                        }
                                    </td>
                                </tr>
                            `,
                        )}
                    </tbody>
                </table>
            </div>
        `;
    }
}

customElements.define('d3-polls-view', PollsView);
